import { ChevronDown, History } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { formatDateTime } from '@/shared/lib/format';
import { DEFAULT_TIME_ZONE } from '@/shared/lib/time-zone';
import { cn } from '@/shared/lib/utils';

import { usePermissions } from '@/features/auth';

import { useEntityHistory } from '../api/audit-log.queries';
import { useCanSeeHistory } from '../hooks/use-can-see-history';
import { actionLabel } from '../lib/audit-labels';
import { AuditChange } from './audit-change';

/**
 * An item's history: every audited change to it, newest first, with who, when,
 * why and what changed (D-26). Readable with the item's own read permission;
 * values your role may not see (costs, balances, takings) are removed by the
 * backend and said so. Renders nothing without the permission.
 */
export function EntityHistory({
  entityType,
  entityId,
  timeZone = DEFAULT_TIME_ZONE,
}: {
  entityType: string;
  entityId: string;
  /** The item's market zone; Palestine time for an item that belongs to no market. */
  timeZone?: string;
}) {
  const canSee = useCanSeeHistory(entityType);
  const canOpenAuditLog = usePermissions().can('reports.audit.read');
  const [page, setPage] = useState(1);
  const history = useEntityHistory(entityType, entityId, page, { enabled: canSee });

  if (!canSee) return null;
  if (history.isLoading) return <Skeleton className="h-32" />;
  if (history.error)
    return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;

  const entries = history.data?.items ?? [];
  if (entries.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="No changes recorded"
        description="Changes made in the Admin appear here with who made them and why."
      />
    );
  }

  const totalPages = history.data?.totalPages ?? 1;
  return (
    <div className="space-y-3 text-xs">
      <ol className="space-y-3" aria-label="History">
        {entries.map((entry) => (
          <li key={entry.id} className="border-border rounded-md border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{actionLabel(entry.action)}</span>
              <span className="text-muted-foreground">
                {formatDateTime(entry.occurredAtUtc, timeZone)} · {entry.actorUserName ?? 'System'}
              </span>
            </div>
            {entry.reason && <p className="mt-1">Reason: {entry.reason}</p>}
            {entry.redactedFields && entry.redactedFields.length > 0 && (
              <p className="text-muted-foreground mt-1">
                {entry.redactedFields.length} recorded value(s) are not shown to your role.
              </p>
            )}
            <div className="mt-2">
              <AuditChange entry={entry} />
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {canOpenAuditLog ? (
          <Link
            to={`/audit-log?type=${encodeURIComponent(entityType)}&id=${encodeURIComponent(entityId)}`}
            className="text-primary hover:underline"
          >
            Open in the audit log
          </Link>
        ) : (
          <span />
        )}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Newer
            </Button>
            <span className="text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Older
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** A History button and dialog, for items shown in a list rather than on their own page. */
export function HistoryButton({
  entityType,
  entityId,
  name,
  timeZone,
}: {
  entityType: string;
  entityId: string;
  name: string;
  /** The item's market zone; Palestine time when omitted. */
  timeZone?: string;
}) {
  const canSee = useCanSeeHistory(entityType);
  const [open, setOpen] = useState(false);
  if (!canSee) return null;

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7"
        onClick={() => setOpen(true)}
        aria-label={`History of ${name}`}
      >
        <History className="size-3.5" aria-hidden />
      </Button>
      {open && (
        <Dialog open onOpenChange={(value) => !value && setOpen(false)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-base">History of {name}</DialogTitle>
              <DialogDescription className="text-xs">
                Every change made in the Admin, newest first.
              </DialogDescription>
            </DialogHeader>
            <EntityHistory entityType={entityType} entityId={entityId} timeZone={timeZone} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/** A titled History section for an item's own page; renders nothing without reports.audit.read. */
export function HistorySection({
  entityType,
  entityId,
  title = 'History',
  description,
  collapsible = false,
  timeZone,
}: {
  entityType: string;
  entityId: string;
  title?: string;
  /** The item's market zone; Palestine time when omitted. */
  timeZone?: string;
  /** Says what is (and is not) recorded here, when the page could suggest more. */
  description?: React.ReactNode;
  /**
   * Starts folded behind its heading, for pages where History is secondary to
   * the work on screen. It is fetched only once opened.
   */
  collapsible?: boolean;
}) {
  const canSee = useCanSeeHistory(entityType);
  const [open, setOpen] = useState(!collapsible);
  if (!canSee) return null;
  const headingId = `history-${entityType}-${entityId}`;
  const bodyId = `${headingId}-body`;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <h2 id={headingId} className="text-sm font-semibold">
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((value) => !value)}
              className="hover:text-primary flex items-center gap-1.5 rounded-sm"
            >
              <ChevronDown
                className={cn('size-4 transition-transform', !open && '-rotate-90 rtl:rotate-90')}
                aria-hidden
              />
              {title}
            </button>
          ) : (
            title
          )}
        </h2>
        {description && <p className="text-muted-foreground text-xs">{description}</p>}
      </div>
      {open && (
        <div id={bodyId}>
          <EntityHistory entityType={entityType} entityId={entityId} timeZone={timeZone} />
        </div>
      )}
    </section>
  );
}
